import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useTranslation } from 'react-i18next';
import { createLearningAssistant } from '../../services/chatApi';
import { Send, RotateCcw, User, Bot, Copy, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €"; 

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const PurchaseCreditsPrompt = () => {
    const { t } = useTranslation();
    const { user } = useAuth();

    return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <h2 className="text-2xl font-semibold mb-2 text-slate-800">{t('purchaseCreditsPrompt.title')}</h2>
            <p className="text-slate-800 mb-1">{t('purchaseCreditsPrompt.yourCredits')} <span className="text-brand-orange font-bold">{user?.credits || 0}</span></p>
            <p className="text-slate-800 mb-6 max-w-md">{t('purchaseCreditsPrompt.description')}</p>
            <Link 
                to="/purchase-credits"
                className="px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
            >
                {t('purchaseCreditsPrompt.purchaseButton')}
            </Link>
        </div>
    );
};

const LearningAssistant = () => {
    const { t, i18n } = useTranslation();
    const { user, setCredits } = useAuth();
    const [subject, setSubject] = useState('');
    const [concept, setConcept] = useState('');
    const [description, setDescription] = useState('');
    const [example, setExample] = useState('');
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isAssistantCreated, setIsAssistantCreated] = useState(false);
    const [error, setError] = useState(null);
    const [currentStep, setCurrentStep] = useState('introduce');
    const messagesEndRef = useRef(null);
    const [copiedMessageIndex, setCopiedMessageIndex] = useState(null);
    

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const validateInputs = () => {
        const errors = [];
        if (!subject.trim()) {
            errors.push(t('learningAssistant.errorSubjectRequired'));
        }
        if (!concept.trim()) {
            errors.push(t('learningAssistant.errorConceptRequired'));
        }
        return errors;
    };

    const handleCreateAssistant = async (e) => {
        e.preventDefault();
        
        const validationErrors = validateInputs();
        if (validationErrors.length > 0) {
            setError(validationErrors.join(', '));
            return;
        }

        setIsLoading(true);
        setError(null);
        setMessages([]);

        try {
            // Initialize the learning assistant session
            const language = i18n.language.split('-')[0];
            const result = await createLearningAssistant({
                subject,
                concept,
                description,
                example,
                language
            });

            // Check for the response and update the UI with it.
            if (result && result.assistant_response) {
                const firstRealMessage = { 
                    role: 'assistant', 
                    content: result.assistant_response 
                };
                setMessages([firstRealMessage]); // Set the FIRST message from the API
                setIsAssistantCreated(true);      // NOW we show the chat interface
            } else {
                throw new Error(result.error || t('learningAssistant.errorGeneric'));
            }
            
            if (result.new_credits !== undefined) {
                setCredits(result.new_credits);
            }
        } catch (err) {
            setError(err.message || t('learningAssistant.errorGeneric'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!input.trim() || isLoading) return;

        const userMessage = { role: 'user', content: input };
        const updatedMessages = [...messages, userMessage];
        setMessages(updatedMessages);
        const previousMessages = [...messages]; 
        setInput('');
        setIsLoading(true);

        try {
            const language = i18n.language.split('-')[0];
            const result = await createLearningAssistant({
                subject,
                concept,
                description,
                example,
                messages: updatedMessages,
                user_input: input,
                language
            });

            if (result.assistant_response) {
                const assistantMessage = { role: 'assistant', content: result.assistant_response };
                setMessages([...updatedMessages, assistantMessage]);
                
                // Update current step based on response content
                
            }

            if (result.new_credits !== undefined) {
                setCredits(result.new_credits);
            }
        } catch (err) {
            setError(err.message || t('learningAssistant.errorGeneric'));
            setMessages(previousMessages);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClearConversation = () => {
        setMessages([]);
        setIsAssistantCreated(false);
        setCurrentStep('introduce');
        setError(null);
    };

    const handleResetForm = () => {
        setSubject('');
        setConcept('');
        setDescription('');
        setExample('');
        setMessages([]);
        setIsAssistantCreated(false);
        setCurrentStep('introduce');
        setError(null);
    };

    const handleCopy = async (content, index) => {
        try {
            await navigator.clipboard.writeText(content);
            setCopiedMessageIndex(index); // Show "Copied!" for this message
            // Hide the "Copied!" status after 2 seconds
            setTimeout(() => {
                setCopiedMessageIndex(null);
            }, 2000);
        } catch (err) {
            console.error('Failed to copy message: ', err);
            // Optionally, show an error to the user
        }
    };


    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
            {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) ? (
                <SubscribePrompt />
            ) : user?.credits <= 0 ? (
                <PurchaseCreditsPrompt />
            ) : (
                <div className="max-w-4xl mx-auto">
                    {/* Header */}
                    <div className="mb-8">
                        <h3 className="text-3xl font-bold text-brand-orange mb-2">
                            {t('learningAssistant.title')}
                        </h3>
                        <p className="text-brand-dark">
                            {t('learningAssistant.description')}
                        </p>
                        
                    </div>

                    {!isAssistantCreated ? (
                        /* Input Form */
                        <form onSubmit={handleCreateAssistant} className="space-y-6">
                            <div>
                                <label htmlFor="subject" className="block text-sm font-medium mb-2 text-brand-dark">
                                    {t('learningAssistant.subjectLabel')}
                                </label>
                                <input
                                    id="subject"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    placeholder={t('learningAssistant.subjectPlaceholder')}
                                    className="w-full p-3 bg-brand-white text-brand-dark border border-slate-600 focus:ring-2 focus:ring-brand-orange focus:outline-none"
                                    disabled={isLoading}
                                />
                            </div>

                            <div>
                                <label htmlFor="concept" className="block text-sm font-medium mb-2 text-brand-dark">
                                    {t('learningAssistant.conceptLabel')}
                                </label>
                                <input
                                    id="concept"
                                    value={concept}
                                    onChange={(e) => setConcept(e.target.value)}
                                    placeholder={t('learningAssistant.conceptPlaceholder')}
                                    className="w-full p-3 bg-brand-white text-brand-dark border border-slate-600 focus:ring-2 focus:ring-brand-orange focus:outline-none"
                                    disabled={isLoading}
                                />
                            </div>

                            <div>
                                <label htmlFor="description" className="block text-sm font-medium mb-2 text-brand-dark">
                                    {t('learningAssistant.descriptionLabel')}
                                </label>
                                <textarea
                                    id="description"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder={t('learningAssistant.descriptionPlaceholder')}
                                    className="w-full p-3 bg-brand-white text-brand-dark border border-slate-600 focus:ring-2 focus:ring-brand-orange focus:outline-none min-h-[100px] resize-y"
                                    disabled={isLoading}
                                />
                            </div>

                            <div>
                                <label htmlFor="example" className="block text-sm font-medium mb-2 text-brand-dark">
                                    {t('learningAssistant.exampleLabel')}
                                </label>
                                <textarea
                                    id="example"
                                    value={example}
                                    onChange={(e) => setExample(e.target.value)}
                                    placeholder={t('learningAssistant.examplePlaceholder')}
                                    className="w-full p-3 bg-brand-white text-brand-dark border border-slate-600 focus:ring-2 focus:ring-brand-orange focus:outline-none min-h-[100px] resize-y"
                                    disabled={isLoading}
                                />
                            </div>

                            {error && (
                                <div className="text-red-500 bg-red-50 p-3 rounded-md">
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                className="px-6 py-3 bg-brand-orange hover:bg-orange-600 text-white font-semibold rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={isLoading}
                            >
                                {isLoading ? t('learningAssistant.creatingButton') : t('learningAssistant.createButton')}
                            </button>
                        </form>
                    ) : (
                        /* Chat Interface */
                        <div className="flex flex-col h-[600px]">
                            {/* Messages */}
                            <div className="flex-1 overflow-y-auto mb-4 space-y-4">
                                {messages.map((message, index) => (
                                    <div
                                        key={index}
                                        className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                    >
                                        <div className={`flex items-start space-x-2 max-w-3xl ${message.role === 'user' ? 'flex-row-reverse space-x-reverse bg-gray-200' : 'border border-brand-dark bg-brand-white relative'}`}>
                                            
                                            <div
                                                className={`px-4 py-2 rounded-lg ${
                                                    message.role === 'user'
                                                        ? 'bg-gray-200'
                                                        : 'border border-brand-dark bg-brand-white'
                                                }`}
                                            >

                                                <ReactMarkdown
                                                    children={message.content}
                                                    components={{
                                                        code({ node, inline, className, children, ...props }) {
                                                            const match = /language-(\w+)/.exec(className || '');
                                                            return !inline && match ? (
                                                                <SyntaxHighlighter
                                                                    children={String(children).replace(/\n$/, '')}
                                                                    style={vscDarkPlus} // Your chosen theme
                                                                    language={match[1]}
                                                                    PreTag="div"
                                                                    {...props}
                                                                />
                                                            ) : (
                                                                <code className={className} {...props}>
                                                                    {children}
                                                                </code>
                                                            );
                                                        }
                                                    }}
                                                />
                                            </div>

                                            {message.role === 'assistant' && (
                                                <button
                                                    onClick={() => handleCopy(message.content, index)}
                                                    className={`absolute top-1 right-1 p-1 rounded-md transition-colors ${
                                                        copiedMessageIndex === index
                                                            ? 'bg-green-500 text-white' // Highlight when copied
                                                            : 'bg-slate-200 hover:bg-brand-orange hover:text-white' // Default and hover
                                                    }`}
                                                    title={copiedMessageIndex === index ? t('learningAssistant.copied') : t('learningAssistant.copyToClipboard')} // i18n for tooltips
                                                >
                                                    {copiedMessageIndex === index ? (
                                                        <Check size={16} />
                                                    ) : (
                                                        <Copy size={16} />
                                                    )}
                                                </button>
                                            )}




                                        </div>
                                    </div>
                                ))}
                                {isLoading && (
                                    <div className="flex justify-start">
                                        <div className="flex items-start space-x-2 max-w-3xl">
                                            <div className="p-2 rounded-full bg-brand-dark">
                                                <Bot size={16} className="text-white" />
                                            </div>
                                            <div className="px-4 py-2 rounded-lg bg-brand-white text-brand-dark border border-slate-300">
                                                <div className="animate-pulse">{t('learningAssistant.thinking')}</div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* Input Form */}
                            <form onSubmit={handleSendMessage} className="flex space-x-4">
                                <input
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder={t('learningAssistant.chatPlaceholder')}
                                    className="flex-1 p-3 bg-brand-white text-brand-dark border border-slate-600 focus:ring-2 focus:ring-brand-orange focus:outline-none"
                                    disabled={isLoading}
                                />
                                <button
                                    type="submit"
                                    className="px-4 py-3 bg-brand-orange hover:bg-orange-600 text-white font-semibold rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={isLoading || !input.trim()}
                                >
                                    <Send size={18} />
                                </button>
                            </form>

                            {/* Action Buttons */}
                            <div className="flex space-x-2 mt-4">
                                <button
                                    onClick={handleClearConversation}
                                    className="px-4 py-2 border border-gray-600 hover:bg-brand-orange text-brand-dark font-semibold rounded-md transition-colors"
                                >
                                    {t('learningAssistant.clearButton')}
                                </button>
                                <button
                                    onClick={handleResetForm}
                                    className="px-4 py-2 border border-gray-600 hover:bg-brand-orange text-brand-dark font-semibold rounded-md transition-colors"
                                >
                                    {t('learningAssistant.newAssistantButton')}
                                </button>
                            </div>

                            {error && (
                                <div className="text-red-500 bg-red-50 p-3 rounded-md mt-2">
                                    {error}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default LearningAssistant;
